// Spatial audio system — pooled voices, bus routing, distance culling.
// Engine feature: game loads sounds and calls play(). Engine handles the rest.

const POOL_SIZE = 24;
const CULL_DISTANCE_SQ = 60 * 60; // sounds beyond 60 units are not played

// --- Types ---

export type PlayOptions = {
  volume?: number;       // 0–1 (default 1)
  rate?: number;         // playback rate (default 1)
  bus?: "sfx" | "music" | "ambient"; // routing bus (default "sfx")
  refDistance?: number;  // 100% volume distance (default 1)
  maxDistance?: number;  // silence distance (default 50)
  rolloff?: number;      // rolloff factor (default 1)
};

// --- Voice pool ---
// Pre-allocated voice slots. Each voice owns a gain + panner chain.
// The source node is recreated per play (Web Audio requires this),
// but gain and panner are reused — no GC pressure on the hot path.

type Voice = {
  gain: GainNode;
  panner: PannerNode;
  source: AudioBufferSourceNode | null;
  endTime: number; // ctx.currentTime when this voice finishes
};

// --- Audio system ---

export class AudioSystem {
  private ctx: AudioContext;
  private buffers = new Map<string, AudioBuffer>();
  private loading = new Map<string, Promise<AudioBuffer>>();
  private voices: Voice[];
  private resumed = false;

  // Bus routing: master → sfx / music / ambient
  readonly master: GainNode;
  readonly sfx: GainNode;
  readonly music: GainNode;
  readonly ambient: GainNode;

  // Listener position (cached for distance culling)
  private lx = 0; private ly = 0; private lz = 0;

  constructor() {
    this.ctx = new AudioContext();

    // Bus hierarchy
    this.master = this.ctx.createGain();
    this.master.connect(this.ctx.destination);
    this.sfx = this.ctx.createGain();
    this.sfx.connect(this.master);
    this.music = this.ctx.createGain();
    this.music.connect(this.master);
    this.ambient = this.ctx.createGain();
    this.ambient.connect(this.master);

    // Pre-allocate voice pool
    this.voices = [];
    for (let i = 0; i < POOL_SIZE; i++) {
      const gain = this.ctx.createGain();
      const panner = this.ctx.createPanner();
      panner.panningModel = "HRTF";
      panner.distanceModel = "linear";
      panner.refDistance = 1;
      panner.maxDistance = 50;
      panner.rolloffFactor = 1;
      gain.connect(panner).connect(this.sfx);
      this.voices.push({ gain, panner, source: null, endTime: 0 });
    }

    // Resume on any user gesture
    const resume = () => {
      if (this.resumed) return;
      this.ctx.resume().then(() => { this.resumed = true; });
    };
    for (const evt of ["mousedown", "pointerdown", "keydown", "touchstart"]) {
      document.addEventListener(evt, resume, { passive: true });
    }
  }

  /** Load a sound from a URL. */
  load(name: string, url: string): void {
    if (this.buffers.has(name) || this.loading.has(name)) return;
    const promise = fetch(url)
      .then(r => r.arrayBuffer())
      .then(data => this.ctx.decodeAudioData(data))
      .then(buf => { this.buffers.set(name, buf); return buf; });
    this.loading.set(name, promise);
  }

  /** Load a pre-decoded buffer (procedural sounds). */
  loadBuffer(name: string, buffer: AudioBuffer): void {
    this.buffers.set(name, buffer);
  }

  /**
   * Play a sound. Spatial if position provided, non-spatial otherwise.
   * Returns false if culled by distance or no voice available.
   */
  play(name: string, position?: { x: number; y: number; z: number }, opts?: PlayOptions): boolean {
    if (!this.resumed) return false;
    const buf = this.buffers.get(name);
    if (!buf) return false;

    // Distance culling for spatial sounds
    if (position) {
      const dx = position.x - this.lx;
      const dy = position.y - this.ly;
      const dz = position.z - this.lz;
      if (dx * dx + dy * dy + dz * dz > CULL_DISTANCE_SQ) return false;
    }

    // Find a free voice (finished playing) or steal the oldest
    const now = this.ctx.currentTime;
    let voice: Voice | null = null;
    let oldestTime = Infinity;
    let oldestIdx = 0;

    for (let i = 0; i < this.voices.length; i++) {
      if (this.voices[i].endTime <= now) {
        voice = this.voices[i];
        break;
      }
      if (this.voices[i].endTime < oldestTime) {
        oldestTime = this.voices[i].endTime;
        oldestIdx = i;
      }
    }

    if (!voice) {
      // Steal oldest voice
      voice = this.voices[oldestIdx];
      if (voice.source) {
        voice.source.stop();
        voice.source.disconnect();
      }
    }

    // Route to correct bus
    const busName = opts?.bus ?? "sfx";
    const busNode = busName === "music" ? this.music : busName === "ambient" ? this.ambient : this.sfx;
    voice.panner.disconnect();
    voice.panner.connect(busNode);

    // Configure panner
    if (position) {
      voice.panner.positionX.value = position.x;
      voice.panner.positionY.value = position.y;
      voice.panner.positionZ.value = position.z;
      voice.panner.refDistance = opts?.refDistance ?? 1;
      voice.panner.maxDistance = opts?.maxDistance ?? 50;
      voice.panner.rolloffFactor = opts?.rolloff ?? 1;
    } else {
      // Non-spatial: place at listener position
      voice.panner.positionX.value = this.lx;
      voice.panner.positionY.value = this.ly;
      voice.panner.positionZ.value = this.lz;
    }

    // Configure gain
    voice.gain.gain.value = opts?.volume ?? 1;

    // Create source (Web Audio requires a new source per play)
    const source = this.ctx.createBufferSource();
    source.buffer = buf;
    source.playbackRate.value = opts?.rate ?? 1;
    source.connect(voice.gain);
    source.start();

    voice.source = source;
    voice.endTime = now + buf.duration / (opts?.rate ?? 1) + 0.05;

    return true;
  }

  /** Update listener position and orientation. Call once per frame. */
  setListener(x: number, y: number, z: number, yaw: number, pitch: number): void {
    this.lx = x; this.ly = y; this.lz = z;
    const listener = this.ctx.listener;
    if (listener.positionX) {
      listener.positionX.value = x;
      listener.positionY.value = y;
      listener.positionZ.value = z;
    } else {
      listener.setPosition(x, y, z);
    }
    const fx = Math.sin(-yaw) * Math.cos(pitch);
    const fy = Math.sin(pitch);
    const fz = Math.cos(-yaw) * Math.cos(pitch);
    if (listener.forwardX) {
      listener.forwardX.value = fx;
      listener.forwardY.value = fy;
      listener.forwardZ.value = fz;
      listener.upX.value = 0;
      listener.upY.value = 1;
      listener.upZ.value = 0;
    } else {
      listener.setOrientation(fx, fy, fz, 0, 1, 0);
    }
  }

  /** Check if a sound is loaded. */
  hasSound(name: string): boolean {
    return this.buffers.has(name);
  }

  /** Set bus volume (0–1). */
  setVolume(bus: "master" | "sfx" | "music" | "ambient", v: number): void {
    this[bus].gain.value = v;
  }

  /** Get the AudioContext (for procedural sound generation). */
  getContext(): AudioContext {
    return this.ctx;
  }

  destroy(): void {
    for (const v of this.voices) {
      if (v.source) { v.source.stop(); v.source.disconnect(); }
      v.gain.disconnect();
      v.panner.disconnect();
    }
    this.voices.length = 0;
    this.ctx.close();
  }
}
